import { useReducer, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteTask } from "@dashboard/hooks/useTaskQuery";
import { useDeleteGroup } from "@dashboard/hooks/useListQuery";
import { useDeleteTaskImage, taskImageKeys } from "@dashboard/hooks/useTaskImageQuery";

export type ListDeletionState = {
    tasks: number[];
    groups: number[];
    images: { imageId: number; taskId: number }[];
};

export interface ListDeletionsApi {
    state: ListDeletionState;
    queueTask: (id: number) => void;
    queueGroup: (id: number) => void;
    queueImage: (imageId: number, taskId: number) => void;
    flush: () => Promise<{ ok: boolean; error?: unknown }>;
    reset: () => void;
    hasQueued: () => boolean;
}

const EMPTY_STATE: ListDeletionState = { tasks: [], groups: [], images: [] };

export function useListDeletions(): ListDeletionsApi {
    // Use a ref as the source of truth so that hasQueued() is synchronously
    // accurate even between a setState call and the next render.
    const stateRef = useRef<ListDeletionState>(EMPTY_STATE);
    const [, forceRender] = useReducer((n: number) => n + 1, 0);

    const update = (next: ListDeletionState) => {
        stateRef.current = next;
        forceRender();
    };

    const deleteTaskMutation = useDeleteTask();
    const deleteGroupMutation = useDeleteGroup();
    const deleteImageMutation = useDeleteTaskImage();
    const queryClient = useQueryClient();

    const queueTask = (id: number) => {
        const cur = stateRef.current;
        if (cur.tasks.includes(id)) return;
        update({ ...cur, tasks: [...cur.tasks, id] });
    };

    const queueGroup = (id: number) => {
        const cur = stateRef.current;
        if (cur.groups.includes(id)) return;
        update({ ...cur, groups: [...cur.groups, id] });
    };

    const queueImage = (imageId: number, taskId: number) => {
        const cur = stateRef.current;
        if (cur.images.some((i) => i.imageId === imageId)) return;
        update({ ...cur, images: [...cur.images, { imageId, taskId }] });
    };

    const flush = async (): Promise<{ ok: boolean; error?: unknown }> => {
        const snapshot = stateRef.current;
        try {
            const taskIdsBeingDeleted = new Set(snapshot.tasks);
            const standaloneImageDeletions = snapshot.images.filter(
                ({ taskId }) => !taskIdsBeingDeleted.has(taskId),
            );
            await Promise.all(
                standaloneImageDeletions.map(({ imageId, taskId }) =>
                    deleteImageMutation.mutateAsync({ imageId, taskId }).catch((error) => {
                        // 404 = already deleted by another tab; idempotent.
                        if (error?.response?.status === 404) return;
                        throw error;
                    }),
                ),
            );

            await Promise.all(
                snapshot.tasks.map((taskId) =>
                    deleteTaskMutation.mutateAsync(taskId).catch((error) => {
                        if (error?.response?.status === 404) return;
                        throw error;
                    }),
                ),
            );

            if (snapshot.tasks.length > 0) {
                await Promise.all(
                    snapshot.tasks.map((taskId) =>
                        queryClient.invalidateQueries({
                            queryKey: taskImageKeys.listsByTaskId(taskId),
                        }),
                    ),
                );
            }

            await Promise.all(
                snapshot.groups.map((groupId) =>
                    deleteGroupMutation.mutateAsync(groupId).catch((error) => {
                        if (error?.response?.status === 404) return;
                        throw error;
                    }),
                ),
            );

            // Clear queue synchronously on success so callers that read hasQueued()
            // immediately after `await flush()` see the cleared state.
            update(EMPTY_STATE);
            return { ok: true };
        } catch (error) {
            return { ok: false, error };
        }
    };

    const reset = () => update(EMPTY_STATE);

    const hasQueued = () => {
        const cur = stateRef.current;
        return cur.tasks.length > 0 || cur.groups.length > 0 || cur.images.length > 0;
    };

    return {
        state: stateRef.current,
        queueTask,
        queueGroup,
        queueImage,
        flush,
        reset,
        hasQueued,
    };
}
